// router.js — dashboard URL <-> page state.
//
// Owns:
//   - navigate(page, pushState, opts) — switch active page, update
//     URL via history.pushState, toggle .active classes on nav +
//     section elements.
//   - pageFromURL() — parse location.pathname → { page, threadID }.
//
// Both functions are exposed on `window` (window.navigate +
// window.pageFromURL) so inline HTML handlers (onclick="navigate(...)")
// keep working. As page modules and click handlers migrate to
// `import` syntax, the window globals can shrink to just `navigate`
// for the inline-onclick cases.
//
// Per-page state hooks (e.g. handoffsState.expanded for thread
// URLs) are now direct ES module imports. The router is
// intentionally a thin layer over the existing state machine;
// it does NOT own any state of its own. State mutation happens
// via state.activePage = X.
import { state } from "./state.js";
import { handoffsState } from "./page-state.js";

// validPages: the allowlist of `data-page="X"` values that this
// router knows about. Missing entries cause direct /path loads to
// silently fall back to overview while the URL still shows the
// path the user wanted — which presents as a "blank page" since
// no .page section gets .active.
//
// Order doesn't matter, but kept grouped by category for readability:
//   workspace pages | nav extras | settings cluster | governance
const VALID_PAGES = [
  // workspace
  "home",
  "overview",
  "chats",
  "leases",
  "files",
  "events",
  // messaging
  "handoffs",
  "messages",
  // team + analytics
  "team",
  "roles",
  "analytics",
  "history",
  "help",
  // identity cluster
  "account",
  "billing",
  "machines",
  "machine-settings",
  "settings",
  // governance
  "worktrees",
  "policy",
  "priority",
  "coordination",
  "agents",
  "protection",
];

// pageFromURL parses /<page>/<slug?> → { page, threadID }.
// Unknown pages fall through to 'overview'. Only the 'handoffs'
// page consumes the slug today; others get an empty threadID.
export function pageFromURL() {
  const raw = location.pathname
    .replace(/^\//, "")
    .replace(/\/$/, "")
    // The daemon serves the SPA under /ui/ and redirects bare root
    // paths (/spend, /leases, …) to /ui/<path>. After that redirect
    // location.pathname is "ui/spend", so the first segment would
    // parse as the page "ui" — not a valid page — and silently fall
    // back to overview. That's the "click Spend → /spend works, but
    // refresh → /ui/spend shows the homepage" bug. Strip a leading
    // "ui" segment so the bare and /ui/-prefixed forms resolve to the
    // same page.
    .replace(/^ui(\/|$)/, "");
  // Two-segment URLs (e.g. /handoffs/<thread_id>) split into page
  // + slug. Only the page is validated against the route table;
  // slugs are opaque to the router and passed to the page's
  // renderer.
  const parts = raw.split("/");
  let head = parts[0] || "";
  const slug =
    parts.length > 1
      ? decodeURIComponent(parts.slice(1).join("/"))
      : "";
  // Legacy-route alias: the Spend page was renamed to Protection
  // (the page is now the agent-to-agent governance screen, not a
  // billing view). Old /spend bookmarks + links must still resolve,
  // so map the retired page-key onto its replacement here. `aliased`
  // is surfaced so the boot path can rewrite the address bar to the
  // canonical /protection instead of leaving the stale /spend showing.
  let aliased = false;
  if (head === "spend") {
    head = "protection";
    aliased = true;
  }
  if (!VALID_PAGES.includes(head)) {
    return { page: "overview", threadID: "", aliased: false };
  }
  // The thread id rides in ?thread= (see navigate); the /handoffs/<id>
  // slug form is still read for old links.
  const thread = slug || new URLSearchParams(location.search).get("thread") || "";
  return {
    page: head,
    threadID: head === "handoffs" ? thread : "",
    aliased,
  };
}

// routeURL keeps the daemon's /ui prefix on a dashboard path. Without it
// the address bar dropped to /leases and every refresh went through the
// daemon's redirect.
export function routeURL(path) {
  const p = location.pathname;
  return (p === "/ui" || p.startsWith("/ui/") ? "/ui" : "") + path;
}

// navigate(page, pushState, opts?): switch active page.
//
//   page       — page name (must be in VALID_PAGES; unknown silently
//                falls through to overview via no-op)
//   pushState  — false ⇒ don't update history (used by popstate
//                handler to avoid double-pushing). Default: true.
//   opts.threadID — slug passed to handoffs page renderer. For
//                   other pages, ignored.
//
// Dispatching the actual page render isn't this function's job —
// it just toggles .active classes. Per-page MutationObservers
// in src/pages/* watch their section's class and fire start() /
// stop() in response.
export function navigate(page, pushState, opts) {
  const threadID = opts && opts.threadID ? String(opts.threadID) : "";
  // When the page hasn't changed but the URL slug (thread id) has,
  // skip the activePage-equality bail-out so we re-render with the
  // new thread context.
  const sameSlug =
    !threadID ||
    (page === "handoffs" && handoffsState.expanded === threadID);
  if (state.activePage === page && sameSlug) return;

  state.activePage = page;
  document.querySelectorAll(".nav-item").forEach((el) => {
    el.classList.toggle("active", el.dataset.page === page);
  });
  document.querySelectorAll(".page").forEach((el) => {
    const isTarget = el.dataset.page === page;
    el.classList.toggle("active", isTarget);
    if (isTarget) {
      el.classList.remove("page-enter");
      void el.offsetWidth;
      el.classList.add("page-enter");
    }
  });
  // Update URL. Routed pages get /<page>/<id>; bare landings stay
  // /<page>. Overview is "/" with no page suffix.
  if (pushState !== false) {
    let url;
    if (page === "overview") {
      url = "/";
    } else if (threadID) {
      // A query, not a path segment: the page loads its scripts by
      // relative URL, so /handoffs/<id> resolved them under /handoffs/
      // and a refresh there loaded no dashboard at all.
      url = "/" + page + "?thread=" + encodeURIComponent(threadID);
    } else {
      url = "/" + page;
    }
    history.pushState({ page, threadID }, "", routeURL(url));
  }
  // Sync per-page state from the URL/opts BEFORE the renderer
  // fires. For handoffs: if the URL carries no thread id, drop
  // any stale expanded state so renderHandoffsPage shows the
  // list (the user's reported bug was landing on /handoffs and
  // seeing a stale thread detail with no obvious way back to
  // the list).
  if (page === "handoffs") {
    if (threadID) {
      handoffsState.expanded = threadID;
      handoffsState.expandedRows = [];
    } else {
      // Bare /handoffs → list view.
      handoffsState.expanded = null;
      handoffsState.expandedRows = [];
    }
  }
  // renderActivePageForce was a dispatch hook for the central
  // page-render loop in pre-Phase-J dashboard.js. Phase J6+ moved
  // each page's render to its own MutationObserver on the section's
  // .active class, so an explicit dispatch from the router is no
  // longer needed — the .active flip below triggers the render.
}

// installPopstateHandler wires browser back/forward to the router.
// Called once from setupLegacyDashboard's init path. Idempotent
// guard via a window flag prevents double-binding if invoked
// twice (e.g. during hot reload).
export function installPopstateHandler() {
  if (window.AC_ROUTER_POPSTATE_INSTALLED) return;
  window.AC_ROUTER_POPSTATE_INSTALLED = true;
  window.addEventListener("popstate", (e) => {
    const fromURL = pageFromURL();
    const page = (e.state && e.state.page) || fromURL.page;
    const threadID = (e.state && e.state.threadID) || fromURL.threadID;
    state.activePage = ""; // force re-render
    navigate(page, false, { threadID });
  });
}

// Expose for inline HTML handlers. dashboard.js has at least one
// onclick="navigate('settings')" inline in a string-built panel
// (line ~1457 in src/bootstrap.js), so navigate must exist
// on window. pageFromURL is exposed too as a defensive measure —
// it's small and may have other inline callers.
window.navigate = navigate;
window.pageFromURL = pageFromURL;
